import { Clinic, ClinicAdmin, Doctor, Invitation, Patient, Test, Token, type User } from '@/types/database';
import { clinicCrud, createUser, invitationCrud, patientCrud, testCrud, userCrud } from './firebase';

import { arrayUnion, Timestamp } from 'firebase/firestore';
import { SendEmailProps } from './sendEmail/sendEmail';
import { Dict } from '@ajorquera/libs/utils';
import sendEmail from './sendEmail';
import sendSms from './sendSms';
import { HOSTNAME } from '@/env';

export const createUserWithInvitation = async (
  userDetails: Partial<User & { password: string }>,
  invitation: Invitation
) => {
  if (!invitation) throw new Error('Invitation not found');

  const role = invitation.details.role as User['role'];
  const clinicId = invitation.clinicIdForAssignment;
  const extraUserDetails: Partial<User> = {};

  if (role === 'clinic-admin') {
    (extraUserDetails as ClinicAdmin).managedClinicId = clinicId;
  } else if (role === 'doctor') {
    (extraUserDetails as Doctor).associatedClinicIds = arrayUnion(clinicId);
  }

  const newUser = await createUser({ ...userDetails, ...extraUserDetails, role });
  await invitationCrud.delete(invitation.id);

  await sendEmail({
    to: newUser.email as string,
    templateName: `${invitation.details.role}-new` as SendEmailProps['templateName'],
    props: { ...userDetails, invitationId: invitation.id },
  });

  return newUser;
};

export const createPatientTest = async (patient: Patient, createdBy: string) => {
  const test = await testCrud.create({
    status: 'pending-patient',
    patientId: patient.id,
    clinicId: patient.clinicId,
    createdBy,
    sentAt: Timestamp.now()
  });
  
  patientCrud.update(patient.id, {
    accessTokens: arrayUnion(createToken(24*7, test.id)),
    status: 'pending-patient'
  });

  return test;
}


export const createPatientInvitation = async (user: Partial<Patient>, invitedBy: string, clinicId: string, token: string) => {
  const patient = await patientCrud.create({
    status: 'pending-patient',
    clinicId: clinicId,
    name: user.name as string,
    email: user.email as string,
    phone: user.phone as string,
    accessTokens: arrayUnion(),
    lastName: user.lastName as string,
    dob: Timestamp.fromDate(user.dob as Date),
  });

  const newTest = await createPatientTest(patient, invitedBy);

  const clinic = await clinicCrud.read(patient.clinicId as string) as Clinic;
  
  try {
    await sendEmail({
      to: patient.email as string,
      templateName: "invitation-patient",
      props: { ...patient, testId: newTest.id, facilityName: clinic.name },
    });
  } catch (error: any) {
    return Promise.reject({...error, type: 'email'});
  }

  const link = `${HOSTNAME}/test/${newTest.id}`;
  const phone = patient.phone?.startsWith('+') ? patient.phone : `+1${patient.phone}`;

  try {
    //await sendSms(phone as string, 'patient-new-test', {facilityName: clinic.name, link}, {headers: {'Authorization': `Bearer ${token}`}});

  } catch (error: any) {
    return Promise.reject({...error, type: 'sms'});
  }

  return patient;
};

export const createToken = (expiresInHours = 24, resourceId?: string): Token => {
  if (typeof expiresInHours !== 'number' || expiresInHours <= 0) {
    expiresInHours = 24;
  }

  // Generate a simple unique token string.
  // In a production environment, consider using a cryptographically secure random string
  // or a UUID generator (e.g., `crypto.randomUUID()` in modern browsers, or a library).
  const token = Math.random().toString(36).substring(2) + Date.now().toString(36);

  const createdAt = Timestamp.now();
  const expiresAt = Timestamp.fromDate(new Date(createdAt.toDate().getTime() + expiresInHours * 60 * 60 * 1000)); // Add hours in milliseconds

  return {
    token: token,
    expiresAt: expiresAt,
    createdAt: createdAt,
    ...(resourceId && {resourceId})
  };
};

export const createInvitation = async (details: Dict, invitedById: string, clinicId?: string) => {
  const token = createToken();

  const newInvitation = await invitationCrud.create({
    status: 'pending',
    details,
    clinicIdForAssignment: clinicId,
    invitationToken: token,
    invitedBy: invitedById,
  } as Invitation);

  await sendEmail({
    to: details.email as string,
    templateName: `invitation-${details.role}` as SendEmailProps['templateName'],
    props: { ...details, invitationId: newInvitation.id },
  });

  return newInvitation;
};

export const createClinic = async (clinic: Record<string, unknown>, createdBy: string) => {
  const newClinic = await clinicCrud.create({
    name: clinic.name,
    phone: clinic.phone,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    createdBy,
    clinicAdminIds: [],
    doctorIds: [],
  } as Omit<Clinic, 'id'>);

  await createInvitation({ name: clinic.clinicAdminName, email: clinic.email, role: 'clinic-admin', facilityName: clinic.name }, createdBy, newClinic.id);

  return newClinic;
};

export const deleteClinic = async (id: string) => {
  return Promise.all([
    clinicCrud.delete(id),
    invitationCrud.deleteAll(['details.clinicId', '==', id]),
    patientCrud.deleteAll(['clinicId', '==', id]),
    userCrud.updateAll({status: 'inactive'}, ['associatedClinicIds', 'array-contains', id]),
    userCrud.updateAll({status: 'inactive'}, ['managedClinicId', '==', id]),
  ]);
};

export const deleteClinicEntity = async (clinicId: string, role: User['role'], id: string) => {
  const entityCrud = clinicCrud.subCollection(`${clinicId}/${role}s`);

  entityCrud.delete(id);
  invitationCrud.deleteAll(['details.id', '==', id]);
};

export const sendReminder = async (patient: Patient, test: Test, token: string) => {
  
  try {
    await sendEmail({
      to: patient.email as string,
      templateName: 'patient-reminder',
      props: { ...patient, testId: test.id }
    });
  } catch (error: any) {
    return Promise.reject({...error, type: 'email'});
  }
  
  const clinic = await clinicCrud.read(patient.clinicId as string) as Clinic;
  const link = `${HOSTNAME}/test/${test.id}`;
  
  const phone = patient.phone?.startsWith('+') ? patient.phone : `+1${patient.phone}`;

  try {
    //await sendSms(phone as string, 'patient-reminder', {facilityName: clinic.name, link}, {headers: {'Authorization': `Bearer ${token}`}});
  } catch (error: any) {
    return Promise.reject({...error, type: 'sms'});
  }

};